// Who someone is in Production & Routing, from the features the platform granted them (`/api/me`):
// the Manager Role (GROUP_MANAGER) holds every feature, the Warehouse Role (GROUP_WAREHOUSE) the
// routing one and its writes, the Viewer Role (GROUP_VIEWER) the routing one alone, and someone in
// the app's group but in none holds none. The server decides (`backend/src/access.ts`); this only
// says it, for the header and for which pages and buttons render.

import { FEATURE_ROUTING, FEATURE_ROUTING_ADMIN, FEATURE_ROUTING_MANAGER, FEATURE_ROUTING_WRITE } from "../reports";

export type RoutingRole = "Manager" | "Warehouse" | "Viewer" | "No access";

/** The role the header shows: "Manager", "Warehouse", "Viewer" or "No access" — the highest held. */
export function routingRole(features: readonly { id: string }[] | undefined): RoutingRole {
  const ids = new Set((features ?? []).map((f) => f.id));
  if (ids.has(FEATURE_ROUTING_MANAGER)) return "Manager";
  if (ids.has(FEATURE_ROUTING) && ids.has(FEATURE_ROUTING_WRITE)) return "Warehouse";
  if (ids.has(FEATURE_ROUTING)) return "Viewer";
  return "No access";
}

/**
 * Whether a person may change anything (`routingWrite`): every write button, editor and automatic
 * write in the pages asks this; the server refuses the rest (403). The pages' payloads say the same
 * (`canWrite`).
 */
export const writesRouting = (features: readonly { id: string }[] | undefined) =>
  (features ?? []).some((f) => f.id === FEATURE_ROUTING_WRITE);

/** An edit a Viewer tries (a cell typed in, Delete, paste, Ctrl+Z…): a small toast, never the red message. */
export const VIEW_ONLY_NOTICE = "View only: you can filter, sort and export, not change.";

/**
 * Said once per page visit: true the first time only, then false — a Viewer clicking into cells and
 * typing to look around sees the toast once, the later tries do nothing.
 */
export const firstTimeOnly = () => {
  let said = false;
  return () => (said ? false : (said = true));
};

/**
 * A write the server refused for the feature (the platform's 403 `feature_forbidden`: a stale tab, a
 * button missed): said as read-only access, never as the raw error. Any error with that status and code.
 */
export const readOnlyRefusal = (e: unknown) =>
  typeof e === "object" &&
  e !== null &&
  (e as { status?: unknown }).status === 403 &&
  (e as { code?: unknown }).code === "feature_forbidden";

/** The words for a refused write in a list or a toast: one sentence. */
export const READ_ONLY_REFUSAL = "View only — nothing was changed.";

/** The pages and Ask AI need the routing feature; without it they do not render at all. */
export const seesRoutingPages = (role: RoutingRole) => role !== "No access";

/** The $ value and managing the lists (the Carrier list too): the Manager Role only. */
export const managesLists = (role: RoutingRole) => role === "Manager";

/** The Admin page (division abbreviations): its own feature, the Manager Role's for now (`access.ts`). */
export const seesAdmin = (features: readonly { id: string }[] | undefined) =>
  (features ?? []).some((f) => f.id === FEATURE_ROUTING_ADMIN);
